<template>
  <div class="tournament-awards" v-if="shouldShowAwards || shouldShowSponsor">
    <!-- Desktop Version - Standard display -->
    <div class="d-none d-sm-block">
      <TournamentAwardsCard
          v-if="shouldRenderAwardsCard"
          :awards="awards"
          :partnerLogo="$store.state.tournament.tournamentSettings.imageUrl"
          :faqsUrl="$store.state.tournament.tournamentSettings.faqsUrl"
          :showAwards="shouldShowAwards"
          :showSponsor="shouldShowSponsor"
      />
      <!-- No awards message -->
      <div v-else class="no-awards">
        {{ $t('noAwardsAvailable') }}
      </div>
    </div>

    <!-- Enhanced Mobile Version -->
    <div class="d-block d-sm-none mt-5">

      <!-- Enhanced awards list for mobile -->
      <div v-if="awards.length > 0" class="mobile-awards-container">
        <!-- Top 3 awards visualization (medal stand) -->
        <div v-if="topAwards.length > 0" class="mobile-top-awards">
          <div class="podium-container">
            <!-- Silver (2nd place) -->
            <div class="podium-position silver" v-if="hasRank(2)">
              <div class="medal-icon silver">
                <i class="fa-solid fa-medal"></i>
              </div>
              <div class="position-number">2</div>
              <div class="podium-block silver-block"></div>
            </div>

            <!-- Gold (1st place) -->
            <div class="podium-position gold" v-if="hasRank(1)">
              <div class="medal-icon gold">
                <i class="fa-solid fa-medal"></i>
              </div>
              <div class="position-number">1</div>
              <div class="podium-block gold-block"></div>
            </div>

            <!-- Bronze (3rd place) -->
            <div class="podium-position bronze" v-if="hasRank(3)">
              <div class="medal-icon bronze">
                <i class="fa-solid fa-medal"></i>
              </div>
              <div class="position-number">3</div>
              <div class="podium-block bronze-block"></div>
            </div>
          </div>
        </div>

        <!-- Compact awards list for mobile -->
        <div class="mobile-awards-list">
          <div v-for="(award, index) in awards" :key="index" class="mobile-award-item">
            <div class="mobile-award-header">
              <div :class="['mobile-rank-badge', getRankClass(award.rank)]">
                <span class="rank-number">{{ award.rank }}</span>
              </div>
              <div class="mobile-award-title">{{ $t(award.title) }}</div>
            </div>
            <div class="mobile-award-details">
              <p class="mobile-award-description">{{ $t(award.description) }}</p>
              <div v-if="award.value" class="mobile-award-value">
                {{ award.value }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No awards message (mobile) -->
      <div v-else class="mobile-no-awards">
        {{ $t('noAwardsAvailable') }}
      </div>
    </div>
  </div>
</template>

<script>
import TournamentAwardsCard from '@/components/TournamentAwardsCard.vue';
export default {
  name: 'TournamentAwards',
  components: {
    TournamentAwardsCard
  },
  computed: {
    awards() {
      return this.$store.state.tournament?.awards || [];
    },
    topAwards() {
      // Get only top 3 awards for podium visualization
      return this.awards.filter(award => award.rank <= 3);
    },
    shouldShowAwards() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      // If selectedFeatures doesn't exist (old tournaments), show all features
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) {
        return true;
      }
      return selectedFeatures.includes('TOURNAMENT_RANKING_AWARD');
    },
    shouldShowSponsor() {
      const selectedFeatures = this.$store.state.tournament?.selectedFeatures;
      if (!selectedFeatures || !Array.isArray(selectedFeatures)) return true;
      return selectedFeatures.includes("TOURNAMENT_RANKING_SPONSORED");
    },
    shouldRenderAwardsCard() {
      // El card existe si hay awards habilitados + data, O si sponsor está habilitado
      return (this.shouldShowAwards && this.awards.length > 0) || this.shouldShowSponsor;
    }
  },
  methods: {
    getRankClass(rank) {
      switch (rank) {
        case 1:
          return 'gold';
        case 2:
          return 'silver';
        case 3:
          return 'bronze';
        default:
          return 'regular';
      }
    },
    hasRank(rank) {
      return this.awards.some(award => award.rank === rank);
    }
  }
}
</script>

<style lang="scss" scoped>
// Common Variables
$gold-color: #FFD700;
$silver-color: #C0C0C0;
$bronze-color: #CD7F32;
$dark-text: var(--v-mainBlack-base);
$light-text: #646464;

.tournament-awards {
  width: 100%;
  font-family: 'Avenir', sans-serif;

  // Desktop styles
  .awards-title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 20px;
    color: $dark-text;
  }

  .awards-list {
    .award-item {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      padding-bottom: 16px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);

      &:last-child {
        border-bottom: none;
      }

      .award-rank {
        margin-right: 16px;

        .rank-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          border: 1px solid;

          &.gold {
            color: $gold-color;
            border-color: $gold-color;
          }

          &.silver {
            color: $silver-color;
            border-color: $silver-color;
          }

          &.bronze {
            color: $bronze-color;
            border-color: $bronze-color;
          }

          &.regular {
            color: $light-text;
            border-color: $light-text;
          }
        }
      }

      .award-content {
        flex: 1;

        .award-title {
          font-weight: 600;
          margin-bottom: 4px;
          color: $dark-text;
        }

        .award-description {
          font-size: 14px;
          color: $light-text;
        }
      }

      .award-value {
        font-weight: bold;
        color: var(--v-mainGreen-base);
      }
    }
  }

  .no-awards {
    text-align: center;
    padding: 20px;
    color: $light-text;
    font-style: italic;
  }

  // Enhanced Mobile styles
  .mobile-awards-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 16px;
    text-align: center;
    color: $dark-text;
  }

  .mobile-awards-container {
    padding: 0px 0;
  }

  .mobile-top-awards {
    margin-bottom: 20px;

    .podium-container {
      height: 120px;
      display: flex;
      align-items: flex-end;
      justify-content: center;
      position: relative;

      .podium-position {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        width: 80px;

        .medal-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: var(--v-mainWhite-base);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

          i {
            font-size: 18px;
          }

          &.gold {
            color: $gold-color;
          }

          &.silver {
            color: $silver-color;
          }

          &.bronze {
            color: $bronze-color;
          }
        }

        .position-number {
          font-weight: bold;
          font-size: 14px;
          margin-bottom: 6px;
        }

        .podium-block {
          width: 60px;
          border-radius: 6px 6px 0 0;

          &.gold-block {
            height: 80px;
            background: linear-gradient(to bottom, rgba(255, 215, 0, 0.2), rgba(255, 215, 0, 0.1));
            border-top: 2px solid $gold-color;
          }

          &.silver-block {
            height: 60px;
            background: linear-gradient(to bottom, rgba(192, 192, 192, 0.2), rgba(192, 192, 192, 0.1));
            border-top: 2px solid $silver-color;
          }

          &.bronze-block {
            height: 40px;
            background: linear-gradient(to bottom, rgba(205, 127, 50, 0.2), rgba(205, 127, 50, 0.1));
            border-top: 2px solid $bronze-color;
          }
        }
      }
    }
  }

  .mobile-awards-list {
    .mobile-award-item {
      background-color: var(--v-lightGray-base);
      border-radius: 10px;
      padding: 12px;
      margin-bottom: 10px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);

      .mobile-award-header {
        display: flex;
        align-items: center;
        margin-bottom: 8px;

        .mobile-rank-badge {
          min-width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          font-weight: bold;

          .rank-number {
            font-size: 14px;
            font-weight: 700;
          }

          &.gold {
            background-color: rgba(255, 215, 0, 0.15);
            color: darken($gold-color, 10%);
            border: 1px solid rgba(255, 215, 0, 0.5);
          }

          &.silver {
            background-color: rgba(192, 192, 192, 0.15);
            color: darken($silver-color, 10%);
            border: 1px solid rgba(192, 192, 192, 0.5);
          }

          &.bronze {
            background-color: rgba(205, 127, 50, 0.15);
            color: darken($bronze-color, 10%);
            border: 1px solid rgba(205, 127, 50, 0.5);
          }

          &.regular {
            background-color: rgba(0, 0, 0, 0.05);
            color: $light-text;
            border: 1px solid rgba(0, 0, 0, 0.1);
          }
        }

        .mobile-award-title {
          font-weight: 600;
          font-size: 15px !important;
          color: $dark-text;
          flex: 1;
        }
      }

      .mobile-award-details {
        padding-left: 40px;

        .mobile-award-description {
          font-size: 13px !important;
          color: $light-text;
          margin-bottom: 6px;
          line-height: 1.4;
        }

        .mobile-award-value {
          font-weight: 600;
          color: var(--v-mainGreen-base);
          font-size: 14px;
        }
      }
    }
  }

  .mobile-no-awards {
    text-align: center;
    padding: 15px;
    color: $light-text;
    font-style: italic;
    font-size: 14px;
    background-color: rgba(0, 0, 0, 0.02);
    border-radius: 8px;
  }
}
</style>


